// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import type {
  PipelineTaskEnvironment,
  PipelineYAML,
} from "../components/pipelines/PipelineYamlEditor/types";

interface LocaleTimeUnit {
  second: string;
  minute: string;
  hour: string;
  day: string;
}

export const formatTime = (seconds: number, locale = "en"): string => {
  const localeTimeUnitMap: { [key: string]: LocaleTimeUnit } = {
    en: {
      second: "s",
      minute: "m",
      hour: "h",
      day: "d",
    },
    ko: {
      second: "초",
      minute: "분",
      hour: "시간",
      day: "일",
    },
  };
  const localeTimeUnit = localeTimeUnitMap[locale] ?? localeTimeUnitMap.en;
  if (seconds <= 0) {
    return `0${localeTimeUnit.second}`;
  }
  const days = Math.floor(seconds / 86400);
  seconds -= days * 86400;
  const hours = Math.floor(seconds / 3600);
  seconds -= hours * 3600;
  const minutes = Math.floor(seconds / 60);
  seconds -= minutes * 60;
  const timeUnits: string[] = [];
  if (days > 0) {
    timeUnits.push(`${days}${localeTimeUnit.day}`);
  }
  if (hours > 0) {
    timeUnits.push(`${hours}${localeTimeUnit.hour}`);
  }
  if (minutes > 0) {
    timeUnits.push(`${minutes}${localeTimeUnit.minute}`);
  }
  if (seconds > 0) {
    timeUnits.push(`${seconds}${localeTimeUnit.second}`);
  }
  return timeUnits.join(" ");
};

/**
 * Format seconds into a compact fixed-width duration string aligned with
 * the Backend.AI WebUI style: ``HH:MM:SS`` or ``Nd HH:MM:SS``.
 *
 * Examples: ``00:05:02``, ``1d 03:21:57``, ``00:00:00``
 */
export const formatCompactDuration = (totalSeconds: number): string => {
  if (totalSeconds <= 0) return "00:00:00";
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const hms = [hours, minutes, seconds]
    .map((n) => String(n).padStart(2, "0"))
    .join(":");
  return days > 0 ? `${days}d ${hms}` : hms;
};

export function generateRandomString(length: number) {
  let text = "";
  const possible =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text;
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface ContainerImageName {
  host?: string;
  port?: number;
  path?: string;
  tag?: string;
  // Parsed verbatim from the `@arch` suffix of an arbitrary image name, so it
  // is plain text (consumers only display it); do not narrow it to the
  // ImageArchitecture union here.
  architecture?: string;
}

export const parseContainerImageName = (
  containerImageName?: string
): ContainerImageName => {
  const regex = /^([^:/]+)(:[\d]+)?(\/[^:]+)*(:?[^:@]*)?(@[^:]+)?$/;
  const match = containerImageName?.match(regex);
  if (!match) {
    return {};
  }
  const [, host, port, path, tag, architecture] = match;
  return {
    host: host,
    port: port ? Number(port.replace(":", "")) : undefined,
    path: path?.replace("/", ""),
    tag: tag?.replace(":", ""),
    architecture: architecture?.replace("@", ""),
  };
};

export const getDedicatedFolderName = (
  storage?: Record<string, unknown> | null
): string | undefined =>
  storage ? (storage.name as string | undefined) : undefined;

export function getProjectResourceGroup(
  pipeline: PipelineYAML | undefined,
  taskEnvironment: PipelineTaskEnvironment | undefined
): string[] | undefined {
  const x =
    pipeline?.ownership?.scope === "project"
      ? pipeline.ownership.project && taskEnvironment?.["scaling-group"]
        ? [pipeline.ownership.project, taskEnvironment["scaling-group"]]
        : undefined
      : taskEnvironment?.project && taskEnvironment["scaling-group"]
        ? [taskEnvironment.project, taskEnvironment["scaling-group"]]
        : undefined;
  return x;
}

export const classNames = (...classes: (string | undefined)[]) => {
  return classes.filter(Boolean).join(" ");
};

const LEGACY_WEBUI_ENDPOINT_KEYS = [
  "FRONTEND_RUNTIME_ENDPOINT", // 26.3–26.7
  "FRONTEND_RUNTIME_WEBSERVER_ENDPOINT", // pre-26.3
] as const;

let warnedLegacyWebUIEndpointKey: string | null = null;

/**
 * Browser-facing origin of the Backend.AI Webserver (the service that serves
 * the WebUI). Interactive login, WebUI asset fetches (`/resources/*`), and
 * external links to WebUI pages all resolve against it.
 *
 * Reads `FRONTEND_RUNTIME_WEBUI_ENDPOINT`, falling back to the legacy names
 * the variable shipped under before the rename (highest wins) so existing
 * deployment `.env` files keep working. Not to be confused with
 * `BACKENDAI_GATEWAY_ENDPOINT`, which is Django's server-to-server address
 * for the same Webserver and is typically unreachable from a browser.
 */
export const getWebUIEndpoint = (): string | undefined => {
  const config = window.__RUNTIME_CONFIG__;
  if (config.FRONTEND_RUNTIME_WEBUI_ENDPOINT) {
    return config.FRONTEND_RUNTIME_WEBUI_ENDPOINT;
  }
  const legacyKey = LEGACY_WEBUI_ENDPOINT_KEYS.find((key) => config[key]);
  if (!legacyKey) {
    return undefined;
  }
  if (warnedLegacyWebUIEndpointKey !== legacyKey) {
    warnedLegacyWebUIEndpointKey = legacyKey;
    console.warn(
      `${legacyKey} is deprecated; set FRONTEND_RUNTIME_WEBUI_ENDPOINT instead.`
    );
  }
  return config[legacyKey];
};

let warnedMalformedWebUIEndpoint = false;

/**
 * Resolve a WebUI-served asset path (`/resources/*`) against the WebUI
 * endpoint, returning `undefined` instead of throwing when it cannot.
 *
 * Callers render the result into an `<img>`, so they need a value they can
 * swap for their own fallback. `new URL(path, undefined)` throws a `TypeError`
 * rather than yielding a falsy value, and every icon call site sits inside
 * `MainLayoutErrorBoundary` — an unset endpoint would replace the whole page
 * with the error fallback instead of degrading one image.
 *
 * The path is joined *relative* to the endpoint so a WebUI mounted under a
 * subpath (`https://host/webui/`) keeps its prefix. A leading slash resolves
 * against the origin and silently drops it.
 */
export const getWebUIResourceUrl = (path: string): string | undefined => {
  const endpoint = getWebUIEndpoint();
  if (!endpoint) {
    return undefined;
  }
  const base = endpoint.endsWith("/") ? endpoint : `${endpoint}/`;
  try {
    return new URL(path.replace(/^\/+/, ""), base).toString();
  } catch {
    // The endpoint is operator-supplied config published verbatim to the
    // browser, so a malformed value is a deployment mistake, not a bug here.
    // Warn once and degrade rather than taking the page down.
    if (!warnedMalformedWebUIEndpoint) {
      warnedMalformedWebUIEndpoint = true;
      console.warn(
        `Cannot resolve WebUI resources: "${endpoint}" is not a valid URL.`
      );
    }
    return undefined;
  }
};

export const getApiEndpoint = () => {
  return window.location.origin;
};

export const numberMinRule = (min: number, message: string) => () => ({
  validator(form: any, value: string) {
    const numberValue = Number(value);
    if (numberValue < min) {
      return Promise.reject(message);
    }
    return Promise.resolve();
  },
});

export const numberMaxRule = (max: number, message: string) => () => ({
  validator(form: any, value: string) {
    const numberValue = Number(value);
    if (numberValue > max) {
      return Promise.reject(message);
    }
    return Promise.resolve();
  },
});

/**
 * Gets the local timezone of the user's browser.
 * @returns The local timezone of the user's browser, or "UTC" if it cannot be determined.
 */
export function getLocalTimezone(): string {
  const localTimeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
  return localTimeZone || "UTC";
}
